import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { db } from '../firebase/firestore';
import {
  collection,
  doc,
  setDoc,
  getDocs,
  getDoc,
  deleteDoc,
  updateDoc,
} from 'firebase/firestore';
import { useUser } from './userContext';

const CartContext = createContext();
export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const userContext = useUser();
  const telegramUser = userContext?.telegramUser;
  const userId = telegramUser?.id?.toString(); // 🔐 гарантируем строку

  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Получение продуктов по ID
  const fetchProductsByIds = async (docs) => {
    const items = [];

    for (const docSnap of docs) {
      const id = docSnap.id;
      const productRef = doc(db, 'products', id);
      const productSnap = await getDoc(productRef);
      const quantity = docSnap.data()?.quantity || 1;

      if (productSnap.exists()) {
        items.push({ id, ...productSnap.data(), quantity });
      }
    }

    return items;
  };

  // Загрузка корзины
  useEffect(() => {
    const fetchCart = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        const cartRef = collection(db, 'telegramUsers', userId, 'cartItems');
        const snapshot = await getDocs(cartRef);
        const products = await fetchProductsByIds(snapshot.docs);
        setCartItems(products);
      } catch (err) {
        console.error('Ошибка загрузки корзины:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCart();
  }, [userId]);

  // Синхронизация корзины
  const syncCartWithFirestore = useCallback(async (items) => {
    if (!userId) return;

    try {
      const userCartRef = collection(db, 'telegramUsers', userId, 'cartItems');

      const existing = await getDocs(userCartRef);
      for (const docSnap of existing.docs) {
        await deleteDoc(docSnap.ref);
      }

      for (const item of items) {
        await setDoc(doc(userCartRef, item.id), {
          quantity: item.quantity || 1,
        });
      }
    } catch (err) {
      console.error('Ошибка синхронизации корзины:', err);
    }
  }, [userId]);

  useEffect(() => {
    if (!loading && userId) {
      syncCartWithFirestore(cartItems);
    }
  }, [cartItems, loading, userId, syncCartWithFirestore]);

  // Добавить в корзину
  const addToCart = async (product) => {
    if (!product?.id) return;
    if (!userId) {
      console.warn('UserId отсутствует, невозможно добавить в корзину');
      return;
    }

    setCartItems((prev) => {
      const exists = prev.find((item) => item.id === product.id);
      if (exists) {
        return prev.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        return [...prev, { ...product, quantity: 1 }];
      }
    });

    try {
      const itemRef = doc(db, 'telegramUsers', userId, 'cartItems', product.id.toString());
      await setDoc(itemRef, { quantity: 1 }, { merge: true });
    } catch (error) {
      console.error('Ошибка записи в Firestore в addToCart:', error);
    }
  };

  // Обновить количество товара (синоним для updateCartItemQuantity для совместимости)
  const updateQuantity = async (productId, newQuantity) => {
    return updateCartItemQuantity(productId, newQuantity);
  };

  // Обновить количество товара в корзине
  const updateCartItemQuantity = async (productId, newQuantity) => {
    if (!userId || !productId || newQuantity < 1) return;

    setCartItems((prev) =>
      prev.map((item) =>
        item.id === productId ? { ...item, quantity: newQuantity } : item
      )
    );

    try {
      const itemRef = doc(db, 'telegramUsers', userId, 'cartItems', productId.toString());
      if (newQuantity === 0) {
        await deleteDoc(itemRef);
      } else {
        await updateDoc(itemRef, { quantity: newQuantity });
      }
    } catch (error) {
      console.error('Ошибка обновления Firestore в updateCartItemQuantity:', error);
    }
  };

  // Увеличить количество товара
  const increaseQuantity = async (productId) => {
    const currentItem = cartItems.find(item => item.id === productId);
    if (currentItem) {
      await updateCartItemQuantity(productId, currentItem.quantity + 1);
    }
  };

  // Уменьшить количество товара
  const decreaseQuantity = async (productId) => {
    const currentItem = cartItems.find(item => item.id === productId);
    if (currentItem) {
      const newQuantity = currentItem.quantity - 1;
      if (newQuantity <= 0) {
        removeFromCart(productId);
      } else {
        await updateCartItemQuantity(productId, newQuantity);
      }
    }
  };

  // Удалить из корзины
  const removeFromCart = (id) => {
    setCartItems((prev) => prev.filter((item) => item.id !== id));

    if (userId) {
      const itemRef = doc(db, 'telegramUsers', userId, 'cartItems', id);
      deleteDoc(itemRef).catch(error => {
        console.error('Ошибка удаления из Firestore:', error);
      });
    }
  };

  // Очистить корзину
  const clearCart = () => {
    setCartItems([]);
    
    if (userId) {
      // Удаляем все товары из корзины пользователя в Firestore
      const userCartRef = collection(db, 'telegramUsers', userId, 'cartItems');
      getDocs(userCartRef).then(snapshot => {
        snapshot.docs.forEach(docSnap => {
          deleteDoc(docSnap.ref);
        });
      }).catch(error => {
        console.error('Ошибка очистки корзины в Firestore:', error);
      });
    }
  };

  // Получить общее количество товаров в корзине
  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  // Получить общую сумму корзины
  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  // Проверить, есть ли товар в корзине
  const isInCart = (productId) => {
    return cartItems.some(item => item.id === productId);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        updateCartItemQuantity,
        updateQuantity, // ✅ для совместимости с новым кодом
        increaseQuantity,
        decreaseQuantity,
        removeFromCart,
        clearCart,
        getTotalItems,
        getTotalPrice,
        isInCart,
        loading,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};